/* ============================================================
   VEYLA | cart drawer
   One file, two modes. On a real storefront (window.Shopify) it is the only
   thing that writes to the cart: POST /cart/add.js and /cart/change.js, then
   re-read /cart.js and render whatever Shopify says. Off a storefront it keeps
   the same cart in memory so the prototype runs with no backend. No store code
   is imported and no other widget is ever clicked.

   Public API
     window.VYDrawer.add(items)   add [{id, quantity, selling_plan, properties}] and open
     window.VYDrawer.open()       open
     window.VYDrawer.close()      close
     window.VYDrawer.pull()       re-read the real cart (live mode only)
     window.VYDrawer.live         true when it is talking to a real cart
     window.__VY_CART             the rendered line array
   ============================================================ */
(() => {
  /* same rule as the page: speak the Vitals conversion when it exists,
     USD otherwise. Checked at call time; this file loads before the
     currency layer, so no reference is held. */
  const money = c =>
    (window.VY_CC && window.VY_CC.fmt(c)) || '$' + (c / 100).toFixed(2);
  const el = (tag, cls, html) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  };

  /* LIVE on a real storefront, in-memory on the prototype. The same file runs in
     both, so the page at :8789 and the page on the theme never drift. */
  const LIVE = !!(window.Shopify && window.Shopify.shop);
  const R = window.routes || {};
  const CART_URL   = (R.cart_url        || '/cart')        + '.js';
  const ADD_URL    = (R.cart_add_url    || '/cart/add')    + '.js';
  const CHANGE_URL = (R.cart_change_url || '/cart/change') + '.js';

  /* On the theme the derived images live in theme assets, so their URLs are
     stamped into the page. Locally they are plain relative paths. */
  const IMG = window.VY_IMG || {};
  const img = name => IMG[name] || ('assets/' + name);

  /* ---------- catalogue: real variant ids and prices from the live store ----------

     The serum's bundle tiers are NOT listed here by hand. Since 2026-08-08
     each tier is its own variant on the product, and their ids live in
     shared/vy-offer.js, which is the one file allowed to know a price. The
     entries are derived from it below, so adding a tier or repricing one is
     still a single-file change and this drawer cannot drift out of step with
     the buybox that filled it.

     The two ids that remain hardcoded are the single-tube variants: the
     client's own product and ours as it was before the bundle option. Neither
     of our surfaces adds them any more, but a shopper can arrive here with one
     already in the cart from another page on the store, and the drawer should
     name it properly rather than fall back to the raw store title "(Copy)". */
  const SERUM_ID      = 50715117191335;   // the client's product, single tube
  const SERUM_COPY_ID = 51104449822887;   // ours, pre-bundle single tube
  const SHAMPOO_ID  = 50884911005863;
  const SHIPPROT_ID = 48153571754151;
  const SCARSTICK_ID = 48782945353895;

  /* Read as a function, never captured in a const. This file loads BEFORE
     shared/vy-offer.js on both the prototype and the theme, so a snapshot
     taken here is always an empty object and every price and cadence read off
     it would silently fall back to a default. Same reason money() checks for
     the currency layer at call time. */
  const offer = () => window.VY_OFFER || {};

  const CATALOG = {
    [SERUM_ID]:     { name: 'Grow:Turn Ampoule',        meta: 'Hair growth serum, 100ml', cents: 4995, was: 7995, img: img('product-tube.png') },
    [SERUM_COPY_ID]: { name: 'Grow:Turn Ampoule',       meta: 'Hair growth serum, 100ml', cents: 4995, was: 7995, img: img('product-tube.png') },
    [SHAMPOO_ID]:   { name: 'Grow:Turn 3D Volume Shampoo', meta: 'Scalp wash, 300ml',     cents: 1995, was: 3995, img: img('addon-shampoo.png') },
    [SHIPPROT_ID]:  { name: 'Shipping Protection',      meta: 'Protect your order from damage, loss or theft.', cents: 295, was: 0, img: img('addon-shipping.png') },
    [SCARSTICK_ID]: { name: 'Clinical Scar Repair Stick', meta: 'For post-acne marks',    cents: 2995, was: 4995, img: img('addon-scarstick.jpg') }
  };

  /* One catalogue entry per bundle variant, RESOLVED ON DEMAND rather than
     built once at load.

     cents and was are per LINE UNIT, and a line unit is one whole bundle, so
     incrementing the quantity here buys a second bundle and the maths stays
     right without a special case.

     Looked up per call because the variant ids are not guaranteed to exist
     when this file runs. They are null until the client's team adds the
     Bundle option, and a build that snapshotted the table at load would keep
     serving an empty catalogue for the rest of the session even after the ids
     arrived. Three tiers, so the scan costs nothing. */
  const tierCatalog = id => {
    const tiers = offer().TIERS || {};
    for (const key of Object.keys(tiers)) {
      const t = tiers[key];
      if (t.variantId !== id) continue;
      return {
        name: 'Grow:Turn Ampoule',
        /* The tube count is NOT repeated here. It is the quantity now, and a
           row reading "2 tubes" beside a quantity of 2 reads like four. */
        meta: 'Hair growth serum, 100ml',
        cents: t.totalCents,
        was: t.compareCents,
        /* How many tubes one of these lines is. The header count is a count
           of TUBES, not of lines: a shopper who bought the two-tube bundle
           has two tubes coming, and a drawer that says "1 item" over a line
           that says "2 tubes" reads like a bug. Kept here rather than on the
           line so it also applies to lines read back off a real Shopify cart,
           which know their quantity but not their tube count. */
        units: t.tubes,
        img: img('product-tube.png')
      };
    }
    return null;
  };

  /* The one way to ask about a line's product. Never index CATALOG directly:
     the bundle variants are not in it. */
  const cat = id => CATALOG[id] || tierCatalog(id) || {};
  /* a line Shopify knows about that we do not: fall back to its own data.
     Tested on c.name, not on c itself: cat() always hands back an object so
     that every caller can read a field off it, so a truthiness check here
     would treat an unknown product as known and render a nameless row. */
  const info = l => {
    const c = cat(l.id);
    if (c.name) return { name: c.name, meta: l.meta || c.meta, img: c.img };
    return { name: l.title || 'Item', meta: l.meta || '', img: l.img || '' };
  };

  /* what the dock offers, in order */
  const DOCK = [SHAMPOO_ID, SCARSTICK_ID];

  /* Shipping protection rides along by default: every add() and every cart
     pull slips it in unless she has unticked it, and the tick below the item
     rows takes it out again. The opt-out is page memory only, on purpose: a
     removal holds while she shops this page, and the next load starts back at
     the default. */
  const PROT_ID = SHIPPROT_ID;
  let protOffMem = false;
  const protOff = () => protOffMem;
  const setProtOff = v => { protOffMem = !!v; };
  const protLine = () => cart.find(l => l.id === PROT_ID);
  const nonProt  = () => cart.filter(l => l.id !== PROT_ID);

  /* Read from the offer module rather than restated, so a change to the
     subscription rate cannot land on the buybox and miss the drawer. */
  const SUB = { get pct() { return (offer().SUB && offer().SUB.pct) || 20; } };

  /* ---------- state ---------- */
  const cart = window.__VY_CART = [];

  /* A line can carry unitCents, the price of one of whatever it is, which for
     a bundle variant is the whole bundle.

     lineCents is the older override: the exact amount the buybox charged for
     the whole line, which existed because every tier shared one variant id
     and per-unit maths could not describe a bundle. The buybox stopped
     sending it on 2026-08-08, since a real variant is priced by Shopify. It
     is still read here because lines pulled back off a live cart set it from
     final_line_price, which is the authority on what she is actually paying. */
  const lineUnit  = l => (l.unitCents != null ? l.unitCents : cat(l.id).cents || 0);
  const lineTotal = l => (l.free ? 0 : (l.lineCents != null ? l.lineCents : lineUnit(l) * l.quantity));
  /* full price for the line: our compare-at where we know the product, and
     Shopify's own pre-discount line price for anything else */
  const lineWasTotal = l => {
    const was = cat(l.id).was;
    if (was) return was * l.quantity;
    return l.wasCents != null ? l.wasCents : lineTotal(l);
  };

  const subtotal = () => cart.reduce((s, l) => s + lineTotal(l), 0);
  const rrpTotal = () => cart.reduce((s, l) => s + lineWasTotal(l), 0);
  /* The header count is products, not services: protection stays out of it.
     A bundle line counts as its tube count, so the number matches what she
     is actually being sent. */
  const lineUnits = l => (cat(l.id).units || 1) * l.quantity;

  /* The number beside the steppers is TUBES, not lines, because a shopper who
     bought Buy 1 Get 1 Free expects to see 2 and reads a bare "1" as having
     been shortchanged. The steppers still move in whole bundles, since a
     2-pack is the smallest thing the store sells at that price, so the
     displayed number steps 2, 4, 6. The labels say so rather than leaving a
     screen reader to announce "increase quantity" and jump by two. */
  const stepLabel = (l, up) => {
    const u = cat(l.id).units || 1;
    const noun = u === 1 ? 'one' : `${u} tubes`;
    return `${up ? 'Add' : 'Remove'} ${noun}`;
  };
  const unitCount = () => cart.reduce((s, l) => s + (l.id === PROT_ID ? 0 : lineUnits(l)), 0);
  const hasPlan = () => cart.some(l => l.selling_plan);
  /* The cadence differs per tier now (2 / 4 / 6 months, matched to tube
     count), so the label rides on the line. The fallback comes from the offer
     module rather than a literal, because a hardcoded "every 4 weeks" would
     keep printing a cadence the store stopped honouring on 2026-08-08. */
  const planLabelFallback = () => {
    const o = offer();
    return (o.SUB && o.SUB.plan && o.SUB.plan.label) || 'every 2 months';
  };
  const planLabel = () => (cart.find(l => l.selling_plan && l.planLabel) || {}).planLabel || planLabelFallback();

  /* The six payment marks the main store drawer shows, Shopify's own SVGs
     lifted from the live theme so the two drawers match exactly. */
  const PAY_ICONS = '<svg class="icon icon--full-color" xmlns="http://www.w3.org/2000/svg" role="img" viewBox="0 0 38 24" width="38" height="24" fill="none" aria-labelledby="pi-american_express"><title id="pi-american_express">American Express</title><rect x=".5" y=".5" width="37" height="23" rx="2.5" stroke="#000" stroke-opacity=".07" fill="none"/><path d="M35 0H3C1.3 0 0 1.3 0 3V21C0 22.7 1.4 24 3 24H35C36.7 24 38 22.7 38 21V3C38 1.3 36.6 0 35 0Z" fill="#0071CE" style="fill:#0071CE;fill:color(display-p3 0.0000 0.4431 0.8078);fill-opacity:1;"/><path d="M3 0.5H35C36.3348 0.5 37.5 1.58692 37.5 3V21C37.5 22.4239 36.4239 23.5 35 23.5H3C1.66524 23.5 0.5 22.4131 0.5 21V3C0.5 1.57614 1.57614 0.5 3 0.5Z" stroke="black" stroke-opacity="0.07" style="stroke:black;stroke-opacity:0.07;"/><path d="M25.8662 6.33203V3H31L31.8662 5.5332L32.7334 3H37V14.2002H36.7998L34.8672 16.2656L36.7998 18.3594H37V21.2666H33.5996L31.9336 19.3994L30.2002 21.2666H19.4668V12.666H16L20.2666 3H24.4004L25.8662 6.33203ZM20.5996 20.2656H27V18.5322H22.666V17.3994H26.8662V15.666H22.666V14.5322H27V12.7988H20.5996V20.2656ZM30.5332 16.5322L27 20.2656H29.5996L31.8662 17.8662L34.0664 20.2656H36.7324L33.1992 16.4658L36.7324 12.7988H34.1328L31.8662 15.1992L29.7324 12.7988H27L30.5332 16.5322ZM17.666 11.7324H19.9326L20.5332 10.1992H23.999L24.666 11.7324H26.999L23.666 4.19922H20.999L17.666 11.7324ZM33.5996 4.19922L31.9326 8.86621L30.1992 4.19922H27V11.666H29.0664V6.39941L31 11.666H32.7998L34.7324 6.39941V11.666H36.7324V4.13281L33.5996 4.19922ZM23.2656 8.46582H21.2656L22.2656 5.99902L23.2656 8.46582Z" fill="white" style="fill:white;fill-opacity:1;"/></svg><svg class="icon icon--full-color" version="1.1" xmlns="http://www.w3.org/2000/svg" role="img" x="0" y="0" width="38" height="24" viewBox="0 0 165.521 105.965" xml:space="preserve" aria-labelledby="pi-apple_pay"><title id="pi-apple_pay">Apple Pay</title><path fill="#000" d="M150.698 0H14.823c-.566 0-1.133 0-1.698.003-.477.004-.953.009-1.43.022-1.039.028-2.087.09-3.113.274a10.51 10.51 0 0 0-2.958.975 9.932 9.932 0 0 0-4.35 4.35 10.463 10.463 0 0 0-.975 2.96C.113 9.611.052 10.658.024 11.696a70.22 70.22 0 0 0-.022 1.43C0 13.69 0 14.256 0 14.823v76.318c0 .567 0 1.132.002 1.699.003.476.009.953.022 1.43.028 1.036.09 2.084.275 3.11a10.46 10.46 0 0 0 .974 2.96 9.897 9.897 0 0 0 1.83 2.52 9.874 9.874 0 0 0 2.52 1.83c.947.483 1.917.79 2.96.977 1.025.183 2.073.245 3.112.273.477.011.953.017 1.43.02.565.004 1.132.004 1.698.004h135.875c.565 0 1.132 0 1.697-.004.476-.002.952-.009 1.431-.02 1.037-.028 2.085-.09 3.113-.273a10.478 10.478 0 0 0 2.958-.977 9.955 9.955 0 0 0 4.35-4.35c.483-.947.789-1.917.974-2.96.186-1.026.246-2.074.274-3.11.013-.477.02-.954.022-1.43.004-.567.004-1.132.004-1.699V14.824c0-.567 0-1.133-.004-1.699a63.067 63.067 0 0 0-.022-1.429c-.028-1.038-.088-2.085-.274-3.112a10.4 10.4 0 0 0-.974-2.96 9.94 9.94 0 0 0-4.35-4.35A10.52 10.52 0 0 0 156.939.3c-1.028-.185-2.076-.246-3.113-.274a71.417 71.417 0 0 0-1.431-.022C151.83 0 151.263 0 150.698 0z" /><path fill="#FFF" d="M150.698 3.532l1.672.003c.452.003.905.008 1.36.02.793.022 1.719.065 2.583.22.75.135 1.38.34 1.984.648a6.392 6.392 0 0 1 2.804 2.807c.306.6.51 1.226.645 1.983.154.854.197 1.783.218 2.58.013.45.019.9.02 1.36.005.557.005 1.113.005 1.671v76.318c0 .558 0 1.114-.004 1.682-.002.45-.008.9-.02 1.35-.022.796-.065 1.725-.221 2.589a6.855 6.855 0 0 1-.645 1.975 6.397 6.397 0 0 1-2.808 2.807c-.6.306-1.228.511-1.971.645-.881.157-1.847.2-2.574.22-.457.01-.912.017-1.379.019-.555.004-1.113.004-1.669.004H14.801c-.55 0-1.1 0-1.66-.004a74.993 74.993 0 0 1-1.35-.018c-.744-.02-1.71-.064-2.584-.22a6.938 6.938 0 0 1-1.986-.65 6.337 6.337 0 0 1-1.622-1.18 6.355 6.355 0 0 1-1.178-1.623 6.935 6.935 0 0 1-.646-1.985c-.156-.863-.2-1.788-.22-2.578a66.088 66.088 0 0 1-.02-1.355l-.003-1.327V14.474l.002-1.325a66.7 66.7 0 0 1 .02-1.357c.022-.792.065-1.717.222-2.587a6.924 6.924 0 0 1 .646-1.981c.304-.598.7-1.144 1.18-1.623a6.386 6.386 0 0 1 1.624-1.18 6.96 6.96 0 0 1 1.98-.646c.865-.155 1.792-.198 2.586-.22.452-.012.905-.017 1.354-.02l1.677-.003h135.875" /><g><g><path fill="#000" d="M43.508 35.77c1.404-1.755 2.356-4.112 2.105-6.52-2.054.102-4.56 1.355-6.012 3.112-1.303 1.504-2.456 3.959-2.156 6.266 2.306.2 4.61-1.152 6.063-2.858" /><path fill="#000" d="M45.587 39.079c-3.35-.2-6.196 1.9-7.795 1.9-1.6 0-4.049-1.8-6.698-1.751-3.447.05-6.645 2-8.395 5.1-3.598 6.2-.95 15.4 2.55 20.45 1.699 2.5 3.747 5.25 6.445 5.151 2.55-.1 3.549-1.65 6.647-1.65 3.097 0 3.997 1.65 6.696 1.6 2.798-.05 4.548-2.5 6.247-5 1.95-2.85 2.747-5.6 2.797-5.75-.05-.05-5.396-2.101-5.446-8.251-.05-5.15 4.198-7.6 4.398-7.751-2.399-3.548-6.147-3.948-7.447-4.048" /></g><g><path fill="#000" d="M78.973 32.11c7.278 0 12.347 5.017 12.347 12.321 0 7.33-5.173 12.373-12.529 12.373h-8.058V69.62h-5.822V32.11h14.062zm-8.24 19.807h6.68c5.07 0 7.954-2.729 7.954-7.46 0-4.73-2.885-7.434-7.928-7.434h-6.706v14.894z" /><path fill="#000" d="M92.764 61.847c0-4.809 3.665-7.564 10.423-7.98l7.252-.442v-2.08c0-3.04-2.001-4.704-5.562-4.704-2.938 0-5.07 1.507-5.51 3.82h-5.252c.157-4.86 4.731-8.395 10.918-8.395 6.654 0 10.995 3.483 10.995 8.89v18.663h-5.38v-4.497h-.13c-1.534 2.937-4.914 4.782-8.579 4.782-5.406 0-9.175-3.222-9.175-8.057zm17.675-2.417v-2.106l-6.472.416c-3.64.234-5.536 1.585-5.536 3.95 0 2.288 1.975 3.77 5.068 3.77 3.95 0 6.94-2.522 6.94-6.03z" /><path fill="#000" d="M120.975 79.652v-4.496c.364.051 1.247.103 1.715.103 2.573 0 4.029-1.09 4.913-3.899l.52-1.663-9.852-27.293h6.082l6.863 22.146h.13l6.862-22.146h5.927l-10.216 28.67c-2.34 6.577-5.017 8.735-10.683 8.735-.442 0-1.872-.052-2.261-.157z" /></g></g></svg><svg class="icon icon--full-color" xmlns="http://www.w3.org/2000/svg" role="img" viewBox="0 0 38 24" width="38" height="24" aria-labelledby="pi-google_pay"><title id="pi-google_pay">Google Pay</title><path d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z" fill="#000" opacity=".07"/><path d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32" fill="#FFF"/><path d="M18.093 11.976v3.2h-1.018v-7.9h2.691a2.447 2.447 0 0 1 1.747.692 2.28 2.28 0 0 1 .11 3.224l-.11.116c-.47.447-1.098.69-1.747.674l-1.673-.006zm0-3.732v2.788h1.698c.377.012.741-.135 1.005-.404a1.391 1.391 0 0 0-1.005-2.354l-1.698-.03zm6.484 1.348c.65-.03 1.286.188 1.778.613.445.43.682 1.03.65 1.649v3.334h-.969v-.766h-.049a1.93 1.93 0 0 1-1.673.931 2.17 2.17 0 0 1-1.496-.533 1.667 1.667 0 0 1-.613-1.324 1.606 1.606 0 0 1 .613-1.336 2.746 2.746 0 0 1 1.698-.515c.517-.02 1.03.093 1.49.331v-.208a1.134 1.134 0 0 0-.417-.901 1.416 1.416 0 0 0-.98-.368 1.545 1.545 0 0 0-1.319.717l-.895-.564a2.488 2.488 0 0 1 2.182-1.06zM23.29 13.52a.79.79 0 0 0 .337.662c.223.176.5.269.785.263.429-.001.84-.17 1.146-.472.305-.286.478-.685.478-1.103a2.047 2.047 0 0 0-1.324-.374 1.716 1.716 0 0 0-1.03.294.883.883 0 0 0-.392.73zm9.286-3.75l-3.39 7.79h-1.048l1.281-2.728-2.224-5.062h1.103l1.612 3.885 1.569-3.885h1.097z" fill="#5F6368"/><path d="M13.986 11.284c0-.308-.024-.616-.073-.92h-4.29v1.747h2.451a2.096 2.096 0 0 1-.9 1.373v1.134h1.464a4.433 4.433 0 0 0 1.348-3.334z" fill="#4285F4"/><path d="M9.629 15.721a4.352 4.352 0 0 0 3.01-1.097l-1.466-1.14a2.752 2.752 0 0 1-4.094-1.44H5.577v1.17a4.53 4.53 0 0 0 4.052 2.507z" fill="#34A853"/><path d="M7.079 12.05a2.709 2.709 0 0 1 0-1.735v-1.17H5.577a4.505 4.505 0 0 0 0 4.075l1.502-1.17z" fill="#FBBC04"/><path d="M9.629 8.44a2.452 2.452 0 0 1 1.74.68l1.3-1.293a4.37 4.37 0 0 0-3.065-1.183 4.53 4.53 0 0 0-4.027 2.5l1.502 1.171a2.715 2.715 0 0 1 2.55-1.875z" fill="#EA4335"/></svg><svg class="icon icon--full-color" xmlns="http://www.w3.org/2000/svg" role="img" viewBox="0 0 38 24" width="38" height="24" fill="none" aria-labelledby="pi-master"><title id="pi-master">Mastercard</title><rect x=".5" y=".5" width="37" height="23" rx="2.5" stroke="#000" stroke-opacity=".07" fill="none"/><path d="M35 0H3C1.3 0 0 1.3 0 3V21C0 22.7 1.4 24 3 24H35C36.7 24 38 22.7 38 21V3C38 1.3 36.6 0 35 0Z" fill="#1C1C1C" style="fill:#1C1C1C;fill:color(display-p3 0.1098 0.1098 0.1098);fill-opacity:1;"/><path d="M35 1C36.1 1 37 1.9 37 3V21C37 22.1 36.1 23 35 23H3C1.9 23 1 22.1 1 21V3C1 1.9 1.9 1 3 1H35Z" fill="#232323" style="fill:#232323;fill:color(display-p3 0.1373 0.1373 0.1373);fill-opacity:1;"/><path d="M14.6364 19.2727C18.8538 19.2727 22.2727 15.8538 22.2727 11.6364C22.2727 7.41892 18.8538 4 14.6364 4C10.4189 4 7 7.41892 7 11.6364C7 15.8538 10.4189 19.2727 14.6364 19.2727Z" fill="#EB001B" style="fill:#EB001B;fill:color(display-p3 0.9216 0.0000 0.1059);fill-opacity:1;"/><path d="M23.3637 19.2727C27.5811 19.2727 31 15.8538 31 11.6364C31 7.41892 27.5811 4 23.3637 4C19.1462 4 15.7273 7.41892 15.7273 11.6364C15.7273 15.8538 19.1462 19.2727 23.3637 19.2727Z" fill="#F79E1B" style="fill:#F79E1B;fill:color(display-p3 0.9686 0.6196 0.1059);fill-opacity:1;"/><path d="M22.2727 11.6362C22.2727 9.01797 20.9637 6.72706 19 5.41797C17.0364 6.83615 15.7273 9.12706 15.7273 11.6362C15.7273 14.1452 17.0364 16.5452 19 17.8543C20.9637 16.5452 22.2727 14.2543 22.2727 11.6362Z" fill="#FF5F00" style="fill:#FF5F00;fill:color(display-p3 1.0000 0.3725 0.0000);fill-opacity:1;"/></svg><svg class="icon icon--full-color" xmlns="http://www.w3.org/2000/svg" role="img" viewBox="0 0 38 24" width="38" height="24" fill="none" aria-labelledby="pi-visa"><title id="pi-visa">Visa</title><rect x=".5" y=".5" width="37" height="23" rx="2.5" stroke="#000" stroke-opacity=".07" fill="none"/><path d="M35 0H3C1.3 0 0 1.3 0 3V21C0 22.7 1.4 24 3 24H35C36.7 24 38 22.7 38 21V3C38 1.3 36.6 0 35 0Z" fill="#142FBD" style="fill:#142FBD;fill:color(display-p3 0.0784 0.1843 0.7412);fill-opacity:1;"/><path d="M35 1C36.1 1 37 1.9 37 3V21C37 22.1 36.1 23 35 23H3C1.9 23 1 22.1 1 21V3C1 1.9 1.9 1 3 1H35Z" fill="#1532CB" style="fill:#1532CB;fill:color(display-p3 0.0824 0.1961 0.7961);fill-opacity:1;"/><path d="M29.5944 10.2167H29.2778C28.8556 11.2722 28.5389 11.8 28.2222 13.3833H30.2278C29.9111 11.8 29.9111 11.0611 29.5944 10.2167V10.2167ZM32.6556 16.4444H30.8611C30.7556 16.4444 30.7556 16.4444 30.65 16.3389L30.4389 15.3889L30.3333 15.1778H27.8C27.6944 15.1778 27.5889 15.1778 27.5889 15.3889L27.2722 16.3389C27.2722 16.4444 27.1667 16.4444 27.1667 16.4444H24.95L25.1611 15.9167L28.2222 8.73889C28.2222 8.21111 28.5389 8 29.0667 8H30.65C30.7556 8 30.8611 8 30.8611 8.21111L32.3389 15.0722C32.4444 15.4944 32.55 15.8111 32.55 16.2333C32.6556 16.3389 32.6556 16.3389 32.6556 16.4444V16.4444ZM18.5111 16.1278L18.9333 14.2278C19.0389 14.2278 19.1444 14.3333 19.1444 14.3333C19.8833 14.65 20.6222 14.8611 21.3611 14.7556C21.5722 14.7556 21.8889 14.65 22.1 14.5444C22.6278 14.3333 22.6278 13.8056 22.2056 13.3833C21.9944 13.1722 21.6778 13.0667 21.3611 12.8556C20.9389 12.6444 20.5167 12.4333 20.2 12.1167C18.9333 11.0611 19.3556 9.58333 20.0944 8.84444C20.7278 8.42222 21.0444 8 21.8889 8C23.1556 8 24.5278 8 25.1611 8.21111H25.2667C25.1611 8.84444 25.0556 9.37222 24.8444 10.0056C24.3167 9.79444 23.7889 9.58333 23.2611 9.58333C22.9444 9.58333 22.6278 9.58333 22.3111 9.68889C22.1 9.68889 21.9944 9.79444 21.8889 9.9C21.6778 10.1111 21.6778 10.4278 21.8889 10.6389L22.4167 11.0611C22.8389 11.2722 23.2611 11.4833 23.5778 11.6944C24.1056 12.0111 24.6333 12.5389 24.7389 13.1722C24.95 14.1222 24.6333 14.9667 23.7889 15.6C23.2611 16.0222 23.05 16.2333 22.3111 16.2333C20.8333 16.2333 19.6722 16.3389 18.7222 16.0222C18.6167 16.2333 18.6167 16.2333 18.5111 16.1278V16.1278ZM14.8167 16.4444C14.9222 15.7056 14.9222 15.7056 15.0278 15.3889C15.5556 13.0667 16.0833 10.6389 16.5056 8.31667C16.6111 8.10556 16.6111 8 16.8222 8H18.7222C18.5111 9.26667 18.3 10.2167 17.9833 11.3778C17.6667 12.9611 17.35 14.5444 16.9278 16.1278C16.9278 16.3389 16.8222 16.3389 16.6111 16.3389L14.8167 16.4444ZM5 8.21111C5 8.10556 5.21111 8 5.31667 8H8.90556C9.43333 8 9.85556 8.31667 9.96111 8.84444L10.9111 13.4889C10.9111 13.5944 10.9111 13.5944 11.0167 13.7C11.0167 13.5944 11.1222 13.5944 11.1222 13.5944L13.3389 8.21111C13.2333 8.10556 13.3389 8 13.4444 8H15.6611C15.6611 8.10556 15.6611 8.10556 15.5556 8.21111L12.2833 15.9167C12.1778 16.1278 12.1778 16.2333 12.0722 16.3389C11.9667 16.4444 11.7556 16.3389 11.5444 16.3389H9.96111C9.85556 16.3389 9.75 16.3389 9.75 16.1278L8.06111 9.58333C7.85 9.37222 7.53333 9.05556 7.11111 8.95C6.47778 8.63333 5.31667 8.42222 5.10556 8.42222L5 8.21111Z" fill="white" style="fill:white;fill-opacity:1;"/></svg><svg class="icon icon--full-color" viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg" width="38" height="24" role="img" aria-labelledby="pi-paypal"><title id="pi-paypal">PayPal</title><path opacity=".07" d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z"/><path fill="#fff" d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32"/><path fill="#003087" d="M23.9 8.3c.2-1 0-1.7-.6-2.3-.6-.7-1.7-1-3.1-1h-4.1c-.3 0-.5.2-.6.5L14 15.6c0 .2.1.4.3.4H17l.4-3.4 1.8-2.2 4.7-2.1z"/><path fill="#3086C8" d="M23.9 8.3l-.2.2c-.5 2.8-2.2 3.8-4.6 3.8H18c-.3 0-.5.2-.6.5l-.6 3.9-.2 1c0 .2.1.4.3.4H19c.3 0 .5-.2.5-.4v-.1l.4-2.4v-.1c0-.2.3-.4.5-.4h.3c2.1 0 3.7-.8 4.1-3.2.2-1 .1-1.8-.4-2.4-.1-.5-.3-.7-.5-.8z"/><path fill="#012169" d="M23.3 8.1c-.1-.1-.2-.1-.3-.1-.1 0-.2 0-.3-.1-.3-.1-.7-.1-1.1-.1h-3c-.1 0-.2 0-.2.1-.2.1-.3.2-.3.4l-.7 4.4v.1c0-.3.3-.5.6-.5h1.3c2.5 0 4.1-1 4.6-3.8v-.2c-.1-.1-.3-.2-.5-.2h-.1z"/></svg>';

  /* The checkout button's label, in one place: the shell prints it and the
     click handler restores it after its transient states. Lock rides inline
     so no stylesheet change was needed. */
  const CO_HTML = 'Secure Checkout <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="20" height="20" style="vertical-align:-4px;margin-left:7px" aria-hidden="true"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 018 0v3"/><circle cx="12" cy="15.5" r="1.3" fill="currentColor" stroke="none"/></svg>';

  /* ---------- shell ---------- */
  const scrim = el('div', 'vyd-scrim');
  const drawer = el('aside', 'vyd');
  drawer.setAttribute('role', 'dialog');
  drawer.setAttribute('aria-modal', 'true');
  drawer.setAttribute('aria-label', 'Your cart');
  drawer.hidden = false;
  drawer.innerHTML = `
    <div class="vyd-head">
      <span class="vyd-title">Your cart</span>
      <span class="vyd-count" id="vyd-count">0 items</span>
      <button class="vyd-x" id="vyd-x" type="button" aria-label="Close cart">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
      </button>
    </div>

    <div class="vyd-band" id="vyd-band"></div>
    <div class="vyd-body" id="vyd-body"></div>

    <div class="vyd-dock" id="vyd-dock">
      <div class="vyd-dock-h"><span>Goes with it</span><span id="vyd-dock-note"></span></div>
      <div class="vyd-dock-rail" id="vyd-dock-rail"></div>
    </div>

    <div class="vyd-foot">
      <div id="vyd-lines"></div>
      <div class="vyd-total"><span>Total today</span><b id="vyd-total">$0.00</b></div>
      <button class="vyd-co" id="vyd-co" type="button">${CO_HTML}</button>
      <div class="vyd-pay">
        <span class="vyd-pay-note">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 018 0v3"/></svg>
          Guaranteed safe and secure checkout
        </span>
        <span class="vyd-pay-icons" role="img" aria-label="American Express, Apple Pay, Google Pay, Mastercard, Visa and PayPal accepted">${PAY_ICONS}</span>
      </div>
      <div class="vyd-ladder">
        <span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 3l7.5 3v6c0 4.6-3.1 8.6-7.5 10-4.4-1.4-7.5-5.4-7.5-10V6z"/><path d="M9 12l2.2 2.2L15 10"/></svg>60 days to change your mind</span>
        <span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M4 12a8 8 0 0113.7-5.6M20 12a8 8 0 01-13.7 5.6"/><path d="M17 3v4h-4M7 21v-4h4"/></svg>Nothing to send back</span>
        <span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="6" width="18" height="13" rx="2"/><path d="M3 10h18"/></svg>Cancel any time</span>
      </div>
      <button class="vyd-keep" id="vyd-keep" type="button">Keep shopping</button>
    </div>`;

  document.body.appendChild(scrim);
  document.body.appendChild(drawer);

  const $d = s => drawer.querySelector(s);

  /* ---------- render ---------- */
  function render() {
    const sub = subtotal();
    const rrp = rrpTotal();
    const count = unitCount();

    $d('#vyd-count').textContent = count === 1 ? '1 item' : count + ' items';

    /* mirror the unit count onto any host chrome that asks for it (header cart) */
    document.querySelectorAll('[data-vy-cart-count]').forEach(n => {
      n.textContent = count > 99 ? '99+' : String(count);
      n.hidden = count === 0;
    });

    /* inclusions bar: one line, three facts. No shipping talk anywhere in
       the drawer (Omar, 2026-08-07): checkout owns shipping. The first slot
       carries the page's own social-proof claim instead. */
    $d('#vyd-band').innerHTML = [
      '<b>100,000+</b> happy customers',
      '<b>60 days</b> to change your mind',
      hasPlan() ? `<b>${SUB.pct}% off</b> every refill` : '<b>No</b> minoxidil'
    ].map(t => `<span>${t}</span>`).join('');

    /* items */
    const body = $d('#vyd-body');
    body.innerHTML = '';
    if (!cart.length) {
      body.appendChild(el('div', 'vyd-empty',
        `<h4>Nothing in the cart yet.</h4>
         <p>One minute a day on the scalp, not the strand. Pick a tier and we will take it from there.</p>`));
    } else {
      cart.forEach((l, i) => {
        if (l.id === PROT_ID) return;            // rendered as the tick row below
        const p = info(l);
        const row = el('div', 'vyd-item');
        const badges = [
          l.selling_plan ? `<span class="vyd-item-plan">${l.planLabel || planLabelFallback()} · ${SUB.pct}% off</span>` : '',
          l.free ? '<span class="vyd-item-free">Gift</span>' : ''
        ].filter(Boolean).join('');
        row.innerHTML = `
          <span class="vyd-item-img"><img src="${p.img}" alt=""></span>
          <div class="vyd-item-mid">
            <div class="vyd-item-name">${p.name}</div>
            <div class="vyd-item-meta">${p.meta || ''}</div>
            ${badges ? `<div class="vyd-item-badges">${badges}</div>` : ''}
            <div class="vyd-qty">
              <button type="button" data-dec="${i}" aria-label="${stepLabel(l, false)}">−</button>
              <span>${lineUnits(l)}</span>
              <button type="button" data-inc="${i}" aria-label="${stepLabel(l, true)}">+</button>
            </div>
          </div>
          <div class="vyd-item-right">
            <span class="vyd-item-price">${l.free ? 'Free' : money(lineTotal(l))}</span>
            ${lineWasTotal(l) > lineTotal(l) ? `<span class="vyd-item-was">${money(lineWasTotal(l))}</span>` : ''}
            <button class="vyd-remove" type="button" data-rm="${i}">Remove</button>
          </div>`;
        body.appendChild(row);
      });

      /* protection tick: in the cart = ticked, unticked = taken out but still
         offered. A label, so the whole row toggles it. */
      const pp = CATALOG[PROT_ID];
      const on = !!protLine();
      const prot = el('label', 'vyd-prot' + (on ? ' is-on' : ''));
      prot.innerHTML = `
        <input type="checkbox" id="vyd-prot-tick" ${on ? 'checked' : ''}>
        <span class="vyd-prot-img"><img src="${pp.img}" alt=""></span>
        <span class="vyd-prot-txt">
          <b>${pp.name}</b>
          <small>${pp.meta}</small>
        </span>
        <span class="vyd-prot-price">${money(pp.cents)}</span>`;
      body.appendChild(prot);
    }

    /* upsell dock, hiding anything already in the cart */
    const rail = $d('#vyd-dock-rail');
    rail.innerHTML = '';
    const offered = DOCK.filter(id => !cart.some(l => l.id === id));
    $d('#vyd-dock').style.display = offered.length ? '' : 'none';
    offered.forEach(id => {
      const p = CATALOG[id];
      const card = el('div', 'vyd-up');
      card.innerHTML = `
        <img src="${p.img}" alt="">
        <span><b>${p.name}</b><span>${money(p.cents)} ${p.was ? `<s>${money(p.was)}</s>` : ''}</span></span>
        <button type="button" data-add="${id}">Add</button>`;
      rail.appendChild(card);
    });

    /* value stack */
    const saved = Math.max(0, rrp - sub);
    const lines = [];
    lines.push(`<div class="vyd-line"><span>Subtotal at full price</span><b>${money(rrp)}</b></div>`);
    if (saved) lines.push(`<div class="vyd-line vyd-line--save"><span>Your saving</span><b>− ${money(saved)}</b></div>`);
    /* no Shipping row and no Refills row: shipping belongs to checkout, and
       the cadence already sits on the item's plan badge (Omar, 2026-08-07) */
    $d('#vyd-lines').innerHTML = lines.join('');
    $d('#vyd-total').textContent = money(sub);
  }

  /* ---------- talking to a real cart ---------- */
  const post = (url, body) => fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify(body)
  }).then(r => r.json().then(d => (r.ok ? d : Promise.reject(d))));

  /* Shopify is the source of truth in LIVE mode, so we mirror /cart.js into the
     same line shape the prototype uses and every renderer stays untouched. */
  async function pull() {
    const data = await fetch(CART_URL, { headers: { 'Accept': 'application/json' } }).then(r => r.json());
    cart.length = 0;
    data.items.forEach(it => {
      const plan = it.selling_plan_allocation && it.selling_plan_allocation.selling_plan;
      cart.push({
        id: it.variant_id,
        key: it.key,                       // what /cart/change.js wants, not the id
        quantity: it.quantity,
        unitCents: it.final_price,
        lineCents: it.final_line_price,
        wasCents: it.original_line_price,
        free: it.final_line_price === 0,
        selling_plan: plan ? plan.id : undefined,
        planLabel: plan ? plan.name.replace(/^Deliver /i, '') : undefined,
        title: it.product_title,
        meta: (it.properties && it.properties._zone) ? it.properties._zone
              : (it.variant_title && it.variant_title !== 'Default Title' ? it.variant_title : ''),
        img: it.image
      });
    });
    render();
    ensureProt();
  }

  /* The default holds even for a cart built before this page loaded: any pull
     that finds products but no protection puts it back, unless she opted out.
     The guard flag stops the pull inside from recursing. */
  let ensuring = false;
  async function ensureProt() {
    if (!LIVE || ensuring || protOff() || protLine() || !nonProt().length) return;
    ensuring = true;
    try { await post(ADD_URL, { items: [{ id: PROT_ID, quantity: 1 }] }); await pull(); }
    catch (err) { console.error('[vy-drawer] protection add failed', err); }
    finally { ensuring = false; }
  }

  /* ---------- events ---------- */
  drawer.addEventListener('click', async e => {
    const t = e.target.closest('button');
    if (!t) return;
    const at = t.dataset.inc != null ? +t.dataset.inc
             : t.dataset.dec != null ? +t.dataset.dec
             : t.dataset.rm  != null ? +t.dataset.rm : -1;
    const l = at > -1 ? cart[at] : null;

    if (LIVE && l) {
      const qty = t.dataset.rm != null ? 0 : l.quantity + (t.dataset.inc != null ? 1 : -1);
      drawer.classList.add('is-busy');
      try { await post(CHANGE_URL, { id: l.key, quantity: Math.max(0, qty) }); await pull(); await dropLoneProt(); }
      catch (err) { console.error('[vy-drawer]', err); }
      finally { drawer.classList.remove('is-busy'); }
      return;
    }

    if (t.dataset.inc != null) {
      l.quantity++; delete l.lineCents;      // the buybox total no longer applies
      render();
    }
    else if (t.dataset.dec != null) {
      delete l.lineCents;
      l.quantity > 1 ? l.quantity-- : cart.splice(at, 1);
      dropLoneProt(); render();
    }
    else if (t.dataset.rm != null) { cart.splice(at, 1); dropLoneProt(); render(); }
    else if (t.dataset.add) { add([{ id: +t.dataset.add, quantity: 1 }], false); }
  });

  /* nobody buys protection on its own: when the last product leaves, it goes too */
  async function dropLoneProt() {
    if (!cart.length || !cart.every(l => l.id === PROT_ID)) return;
    if (LIVE) {
      const p = protLine();
      try { await post(CHANGE_URL, { id: p.key, quantity: 0 }); await pull(); }
      catch (err) { console.error('[vy-drawer]', err); }
    } else {
      cart.length = 0;
    }
  }

  /* the protection tick adds or removes the real line */
  drawer.addEventListener('change', async e => {
    if (e.target.id !== 'vyd-prot-tick') return;
    const on = e.target.checked;
    setProtOff(!on);
    if (LIVE) {
      drawer.classList.add('is-busy');
      try {
        if (on) await post(ADD_URL, { items: [{ id: PROT_ID, quantity: 1 }] });
        else { const p = protLine(); if (p) await post(CHANGE_URL, { id: p.key, quantity: 0 }); }
        await pull();
      }
      catch (err) { console.error('[vy-drawer]', err); }
      finally { drawer.classList.remove('is-busy'); }
      return;
    }
    if (on) { if (!protLine()) cart.push({ id: PROT_ID, quantity: 1 }); }
    else {
      const at = cart.findIndex(l => l.id === PROT_ID);
      if (at > -1) cart.splice(at, 1);
    }
    render();
  });

  $d('#vyd-x').addEventListener('click', close);
  $d('#vyd-keep').addEventListener('click', close);
  scrim.addEventListener('click', close);
  document.addEventListener('keydown', e => { if (e.key === 'Escape' && drawer.classList.contains('is-open')) close(); });
  $d('#vyd-co').addEventListener('click', () => {
    const btn = $d('#vyd-co');
    if (LIVE) { btn.textContent = 'Taking you to checkout'; window.location.href = '/checkout'; return; }
    btn.textContent = 'Prototype · no checkout wired';
    setTimeout(() => { btn.innerHTML = CO_HTML; }, 1600);
  });

  /* ---------- api ---------- */
  async function add(items, openAfter = true) {
    /* protection rides along with any product add, unless she opted out */
    const addingProducts = (items || []).some(it => it.id !== PROT_ID);
    const wantProt = addingProducts && !protLine() && !protOff();
    if (LIVE) {
      /* Only the fields Shopify accepts. The display hints the buybox attaches
         (unitCents, meta, planLabel) would be rejected by /cart/add.js, and we
         do not need them: the prices come back from the cart. */
      const payload = (items || []).map(it => {
        const line = { id: it.id, quantity: it.quantity || 1 };
        if (it.selling_plan) line.selling_plan = it.selling_plan;
        if (it.properties) line.properties = it.properties;
        return line;
      });
      await post(ADD_URL, { items: payload });
      /* separate, best-effort: a sold-out or hidden protection variant must
         never take the product add down with it */
      if (wantProt) {
        try { await post(ADD_URL, { items: [{ id: PROT_ID, quantity: 1 }] }); }
        catch (err) { console.error('[vy-drawer] protection add failed', err); }
      }
      await pull();
      bump();
      if (openAfter) open();
      return;
    }
    if (wantProt) items = items.concat([{ id: PROT_ID, quantity: 1 }]);
    (items || []).forEach(it => {
      /* Lines that carry a lineCents override are a priced bundle deal, so two
         of them never merge: folding a 4-tube deal into a 2-tube one would keep
         the first line's total and hand over four tubes for the price of two.
         Plain lines (the add-on, a dock item) still stack normally. */
      const same = it.lineCents == null && cart.find(l =>
        l.id === it.id && l.selling_plan === it.selling_plan && !l.free && l.lineCents == null);
      if (same) same.quantity += it.quantity || 1;
      else cart.push({
        id: it.id,
        quantity: it.quantity || 1,
        selling_plan: it.selling_plan,
        unitCents: it.unitCents,
        lineCents: it.lineCents,
        planLabel: it.planLabel,
        properties: it.properties,
        meta: it.meta
      });
    });
    render();
    bump();
    if (openAfter) open();
  }
  function bump() {
    document.querySelectorAll('[data-vy-cart-open]').forEach(n => {
      n.classList.remove('is-bumped');
      void n.offsetWidth;
      n.classList.add('is-bumped');
    });
  }
  function open() {
    drawer.classList.add('is-open');
    scrim.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    $d('#vyd-x').focus();
  }
  function close() {
    drawer.classList.remove('is-open');
    scrim.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  /* any host element marked [data-vy-cart-open] opens the drawer (header icon) */
  document.addEventListener('click', e => {
    const t = e.target.closest && e.target.closest('[data-vy-cart-open]');
    if (!t) return;
    e.preventDefault();
    open();
  });

  window.VYDrawer = { add, open, close, render, pull: LIVE ? pull : null, live: LIVE };
  document.addEventListener('vy:currency', () => render());
  render();
  // on a real storefront the cart may already have lines in it from another tab
  if (LIVE) pull().catch(err => console.error('[vy-drawer]', err));
})();
